import CallingExtensions from '@hubspot/calling-extensions-sdk';

export interface HubSpotContact {
  objectId: number;
  objectType: string;
  properties?: Record<string, any>;
}

export interface HubSpotEngagementData {
  engagementId: number;
  objectId: number;
  objectType: string;
}

// SDK type definitions don't match actual API, use any for SDK instance
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CallingExtensionsSDK = any;

class HubSpotSDK {
  private sdk: CallingExtensionsSDK | null = null;
  private isInitialized: boolean = false;
  private currentCallId: string | null = null;
  private outgoingCallHandler: ((data: { phoneNumber: string; ownerId?: string; contact?: HubSpotContact }) => void) | null = null;
  private visibilityHandler: ((data: { isMinimized: boolean; isHidden: boolean }) => void) | null = null;

  async initialize(): Promise<void> {
    console.log('[HubSpot SDK] 🚀 Initializing...');

    if (this.isInitialized) {
      console.log('[HubSpot SDK] ✅ Already initialized, skipping');
      return;
    }

    // Check if we're in an iframe (HubSpot context)
    if (window.self === window.top) {
      console.log('[HubSpot SDK] ⚠️  Not in iframe (not embedded in HubSpot), skipping initialization');
      return;
    }

    console.log('[HubSpot SDK] 📱 In iframe context, creating SDK instance...');

    try {
      // Create SDK instance with event handlers
      // v1.0.0+ requires 'new' keyword (class constructor)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.sdk = new (CallingExtensions as any)({
        debugMode: true,
        eventHandlers: {
          onReady: () => {
            console.log('[HubSpot SDK] ✅ SDK ready!');
            this.isInitialized = true;
          },
          onDialNumber: (event: any) => {
            console.log('[HubSpot SDK] 📞 ===== onDialNumber EVENT =====');
            console.log('[HubSpot SDK] Raw event:', event);
            console.log('[HubSpot SDK] Phone Number:', event.phoneNumber);
            console.log('[HubSpot SDK] Owner ID:', event.ownerId);
            console.log('[HubSpot SDK] Object ID:', event.objectId);
            console.log('[HubSpot SDK] Object Type:', event.objectType);
            console.log('[HubSpot SDK] ==============================');

            if (this.outgoingCallHandler) {
              console.log('[HubSpot SDK] 📤 Calling outgoingCallHandler...');
              this.outgoingCallHandler({
                phoneNumber: event.phoneNumber || '',
                ownerId: event.ownerId ? String(event.ownerId) : undefined,
                contact: event.objectId
                  ? {
                      objectId: event.objectId,
                      objectType: event.objectType || 'CONTACT',
                      properties: event,
                    }
                  : undefined,
              });
            } else {
              console.warn('[HubSpot SDK] ⚠️  No outgoingCallHandler registered!');
            }
          },
          onEngagementCreated: (event: any) => {
            console.log('HubSpot engagement created:', event);
          },
          onVisibilityChanged: (event: any) => {
            console.log('HubSpot visibility changed:', event);
            if (this.visibilityHandler) {
              this.visibilityHandler({
                isMinimized: event.isMinimized || false,
                isHidden: event.isHidden || false,
              });
            }
          },
        },
      });

      this.isInitialized = true;
      console.log('[HubSpot SDK] ✅ Initialization complete!');
    } catch (error) {
      console.error('[HubSpot SDK] ❌ Initialization failed:', error);
      console.error('[HubSpot SDK] Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      // Don't throw - allow app to work outside HubSpot
    }
  }

  async notifyCallStarted(payload: {
    phoneNumber: string;
    callId: string;
    direction: 'inbound' | 'outbound';
  }): Promise<void> {
    if (!this.sdk || !this.isInitialized) {
      console.log('HubSpot SDK not available, skipping notifyCallStarted');
      return;
    }

    this.currentCallId = payload.callId;

    try {
      if (payload.direction === 'outbound') {
        this.sdk.outgoingCall({
          createEngagement: false, // Backend creates the engagement via HubSpot API
          phoneNumber: payload.phoneNumber,
          callStartTime: Date.now(),
        });
      } else {
        this.sdk.incomingCall({
          createEngagement: false, // Backend creates the engagement via HubSpot API
          phoneNumber: payload.phoneNumber,
          callStartTime: Date.now(),
        });
      }
    } catch (error) {
      console.error('Failed to notify HubSpot of call start:', error);
    }
  }

  async notifyCallAnswered(): Promise<void> {
    if (!this.sdk || !this.isInitialized || !this.currentCallId) {
      return;
    }

    try {
      this.sdk.callAnswered();
    } catch (error) {
      console.error('Failed to notify HubSpot of call answered:', error);
    }
  }

  async notifyCallEnded(_payload: {
    engagementId?: number;
    hideWidget?: boolean;
  } = {}): Promise<void> {
    if (!this.sdk || !this.isInitialized) {
      return;
    }

    try {
      this.sdk.callEnded({
        callEndStatus: 'COMPLETED',
      });
      this.currentCallId = null;
    } catch (error) {
      console.error('Failed to notify HubSpot of call end:', error);
    }
  }

  async notifyCallCompleted(payload: {
    engagementId: number;
    hideWidget?: boolean;
  }): Promise<void> {
    if (!this.sdk || !this.isInitialized) {
      return;
    }

    try {
      this.sdk.callCompleted({
        engagementId: payload.engagementId,
      });
      this.currentCallId = null;
    } catch (error) {
      console.error('Failed to notify HubSpot of call completion:', error);
    }
  }

  async resizeWidget(dimensions: { width: number; height: number }): Promise<void> {
    if (!this.sdk || !this.isInitialized) {
      return;
    }

    try {
      this.sdk.resizeWidget(dimensions);
    } catch (error) {
      console.error('Failed to resize HubSpot widget:', error);
    }
  }

  onOutgoingCall(handler: (data: { phoneNumber: string; ownerId?: string; contact?: HubSpotContact }) => void): void {
    console.log('[HubSpot SDK] 📝 Registering outgoingCallHandler...');
    this.outgoingCallHandler = handler;
    console.log('[HubSpot SDK] ✅ outgoingCallHandler registered');
  }

  onVisibilityChanged(handler: (data: { isMinimized: boolean; isHidden: boolean }) => void): void {
    this.visibilityHandler = handler;
  }

  async logMessage(message: string, type: 'log' | 'warn' | 'error' = 'log'): Promise<void> {
    // v0.9.x doesn't have logMessage - just console log
    console[type](`[HubSpot] ${message}`);
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  getCurrentCallId(): string | null {
    return this.currentCallId;
  }
}

export const hubspotSDK = new HubSpotSDK();
